'use client'

import { useUserSettings } from '@dustin-web/microsite-components/src/hooks'
import { BrandPageFragment, BrandContentPageFragment } from '@contentsite/src/graphql/generated/graphql-types'

const getCommonProperties = (
  page: BrandPageFragment | BrandContentPageFragment,
  cultureCode: string
) => {
  const about = {
    '@type': 'Thing',
    description: page.metaDescription,
  }

  const keywords = page.metaKeywords
  const headline = page.title
  const inLanguage = cultureCode

  const commonProperties = {
    about,
    keywords,
    headline,
    inLanguage,
  }

  return commonProperties
}

const getImageObject = (imageUrl?: string) => {
  if (!imageUrl) {
    return
  }

  return {
    '@type': 'ImageObject',
    contentUrl: imageUrl,
  }
}

type BrandContentProps = {
  page: BrandContentPageFragment
}

export const BrandContentSchema = ({ page }: BrandContentProps) => {
  const { cultureCode } = useUserSettings()
  const mainImageUrl = page.brandContent.find(x => !!x.image)?.image
  const primaryImageOfPage = getImageObject(mainImageUrl?.url)
  const commonProperties = getCommonProperties(page, cultureCode)
  const data = { ...commonProperties, primaryImageOfPage }

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            ...data,
          }),
        }}
      />
    </div>
  )
}

type BrandProps = {
  page: BrandPageFragment
}

export const BrandPageSchema = ({ page }: BrandProps) => {
  const { cultureCode } = useUserSettings()
  const thumbnail = page.logo
  const commonProperties = getCommonProperties(page, cultureCode)
  const data = { ...commonProperties, thumbnail }

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            ...data,
          }),
        }}
      />
    </div>
  )
}

type SoftCategoryProps = {
  page: {
    title?: string
    metaDescription?: string | null
    metaKeywords?: string | null
  }
}

export const SoftCategoryContentSchema = ({ page }: SoftCategoryProps) => {
  const { cultureCode } = useUserSettings()
  const commonProperties = getCommonProperties(
    page as BrandPageFragment | BrandContentPageFragment,
    cultureCode
  )

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            ...commonProperties,
          }),
        }}
      />
    </div>
  )
}
